/* SSi website – Spanish landing page hero.
 *
 * Two variations, switchable via Tweaks panel:
 *   A. "Greeting overlay"  – same layout as the main hero, minus the
 *      10-flag strip, plus a big playful ¡Hola! chalk-script overlay
 *      and a Spanish-flavoured kicker ("Para Hispanohablantes? No.
 *      For English speakers.") that hooks ads.
 *   B. "Giant word"        – headline flipped into a typographic flex.
 *      Huge display-face "Hablar." sits above the English proposition,
 *      with a translation line underneath. Quieter image treatment,
 *      more editorial.
 *
 * Shared scaffolding: same red brand background, same CTAs, same trust
 * copy. Diff lives in markup order + which flourish appears.
 */
import { Link } from 'react-router-dom';

export function SpanishHero({ onStart, t }) {
  const variant = t.esHeroVariant || 'A';
  return variant === 'B'
    ? <SpanishHeroB onStart={onStart} t={t}/>
    : <SpanishHeroA onStart={onStart} t={t}/>;
}

/* ──────────────────────────────── A ─ greeting overlay ──────────────────────────────── */
function SpanishHeroA({ onStart, t }) {
  return (
    <section className="ssi-esHero ssi-esHero--A" style={esHeroStyles.wrapA}>
      <div className="ssi-esHero__leftA" style={esHeroStyles.leftA}>
        {t.esHeroKicker && <div style={esHeroStyles.kicker}>{t.esHeroKicker}</div>}
        <h1 style={esHeroStyles.h1}>{t.esHeroTitle}</h1>
        <p style={esHeroStyles.lede}>{t.esHeroSub1}</p>
        <p style={esHeroStyles.lede}>{t.esHeroSub2}</p>
        <div style={esHeroStyles.ctas}>
          <Link to="/pay?course=en-es" className="btn btn--onDark">{t.esHeroCta1}</Link>
          <a href="#sample" className="btn" style={esHeroStyles.ghost}>{t.esHeroCta2}</a>
        </div>
        <p style={esHeroStyles.trust}>{t.esHeroTrust}</p>
      </div>
      <div className="ssi-esHero__rightA" style={esHeroStyles.rightA}>
        <img src="/assets/hero_couple_laughing.png?v=6" alt="" style={esHeroStyles.heroImg}/>
        <div className="ssi-esHero__hola" style={esHeroStyles.holaBubble} aria-hidden>
          <span style={esHeroStyles.holaWord}>¡Hola!</span>
          <span style={esHeroStyles.holaTail}/>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────── B ─ giant word ──────────────────────────────── */
function SpanishHeroB({ onStart, t }) {
  return (
    <section className="ssi-esHero ssi-esHero--B" style={esHeroStyles.wrapB}>
      <div className="ssi-esHero__innerB" style={esHeroStyles.innerB}>
        <div className="ssi-esHero__giantWrap" style={esHeroStyles.giantWrap}>
          <span style={esHeroStyles.giantTranslate}>(verb) to speak</span>
          <div style={esHeroStyles.giant} aria-hidden>{t.esHeroGiantWord}</div>
          <div style={esHeroStyles.giantUnderline}/>
        </div>
        <div style={esHeroStyles.copyB}>
          {t.esHeroKicker && <div style={esHeroStyles.kickerB}>{t.esHeroKicker}</div>}
          <h1 style={esHeroStyles.h1B}>{t.esHeroTitle}</h1>
          <p style={esHeroStyles.ledeB}>{t.esHeroSub1}</p>
          <p style={esHeroStyles.ledeB}>{t.esHeroSub2}</p>
          <div style={esHeroStyles.ctas}>
            <Link to="/pay?course=en-es" className="btn btn--onDark">{t.esHeroCta1}</Link>
            <a href="#sample" className="btn" style={esHeroStyles.ghost}>{t.esHeroCta2}</a>
          </div>
          <p style={esHeroStyles.trustB}>{t.esHeroTrust}</p>
        </div>
      </div>
    </section>
  );
}

const esHeroStyles = {
  /* ─── shared ─── */
  kicker: {
    fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase',
    color: '#FFD8D4', fontWeight: 600, marginBottom: 18,
  },
  h1: {
    fontFamily: 'var(--font-display)', fontSize: 64, lineHeight: 1.04,
    color: '#fff', letterSpacing: '-.015em', margin: '0 0 20px', textWrap: 'balance',
  },
  lede: { fontSize: 18, lineHeight: 1.55, color: 'rgba(255,255,255,.92)', margin: '0 0 18px', maxWidth: 540 },
  ctas: { display: 'flex', gap: 12, marginTop: 20, marginBottom: 24 },
  ghost: { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.5)' },
  trust: { fontSize: 13, color: 'rgba(255,255,255,.72)', margin: 0 },

  /* ─── variant A: greeting overlay ─── */
  wrapA: {
    background: 'var(--brandRed)', color: '#fff',
    padding: '88px 0 88px',
    position: 'relative', overflow: 'hidden',
    minHeight: 560,
  },
  leftA: { maxWidth: 600, padding: '0 32px', position: 'relative', zIndex: 2 },
  rightA: {
    position: 'absolute', right: 0, top: 0, bottom: 0,
    width: '50%',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  },
  heroImg: { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' },
  holaBubble: {
    position: 'absolute',
    left: 'calc(10% )', top: 64,
    background: '#FFD84D',
    color: '#490300',
    padding: '18px 28px 22px',
    borderRadius: 18,
    transform: 'rotate(-5deg)',
    boxShadow: '0 18px 40px -14px rgba(0,0,0,.35), 0 2px 0 rgba(0,0,0,.08)',
    zIndex: 3,
  },
  holaWord: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 52,
    lineHeight: 1,
    letterSpacing: '-.02em',
    display: 'inline-block',
    fontStyle: 'italic',
  },
  holaTail: {
    position: 'absolute',
    left: 36, bottom: -12,
    width: 22, height: 22,
    background: '#FFD84D',
    borderBottomLeftRadius: 4,
    borderStyle: 'solid',
    borderWidth: '0 0 2px 2px',
    borderColor: 'transparent',
    transform: 'rotate(-45deg)',
  },

  /* ─── variant B: giant word ─── */
  wrapB: {
    background: 'var(--brandRed)', color: '#fff',
    padding: '80px 0 88px',
    position: 'relative', overflow: 'hidden',
  },
  innerB: {
    maxWidth: 1240, margin: '0 auto', padding: '0 32px',
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56,
    alignItems: 'center',
  },
  giantWrap: { position: 'relative' },
  giantTranslate: {
    display: 'inline-block',
    fontSize: 13,
    color: 'rgba(255,255,255,.7)',
    letterSpacing: '.02em',
    fontStyle: 'italic',
    marginBottom: 4,
    marginLeft: 4,
  },
  giant: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(120px, 15vw, 220px)',
    lineHeight: .95,
    fontWeight: 700,
    letterSpacing: '-.03em',
    color: '#fff',
    margin: 0,
  },
  giantUnderline: {
    width: 80, height: 4,
    background: '#FFD84D',
    borderRadius: 4,
    marginTop: 20,
    marginLeft: 4,
  },
  copyB: { maxWidth: 520 },
  kickerB: {
    fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase',
    color: '#FFD8D4', fontWeight: 600, marginBottom: 16,
  },
  h1B: {
    fontFamily: 'var(--font-display)', fontSize: 44, lineHeight: 1.1,
    color: '#fff', letterSpacing: '-.015em', margin: '0 0 18px', textWrap: 'balance',
  },
  ledeB: {
    fontSize: 16.5, lineHeight: 1.55, color: 'rgba(255,255,255,.92)',
    margin: '0 0 16px', maxWidth: 520,
  },
  trustB: { fontSize: 13, color: 'rgba(255,255,255,.72)', margin: 0 },
};
